import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useEPIInventory, useAssignEPI } from '@/hooks/useRPL';
import { epiAssignmentFormSchema, type EPIAssignmentFormData } from '@/schemas/rplSchemas';
import { api } from '@/lib/apiClient';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

interface EPIAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedEpiId?: string;
  preselectedEmployeeId?: string;
}

export function EPIAssignmentDialog({
  open,
  onOpenChange,
  preselectedEpiId,
  preselectedEmployeeId,
}: EPIAssignmentDialogProps) {
  const { data: availableEpis } = useEPIInventory({ status: 'available' });
  const assignMutation = useAssignEPI();
  const [loading, setLoading] = useState(false);

  // Fetch employees
  const { data: employees } = useQuery({
    queryKey: ['profiles-active'],
    queryFn: async () => {
      const { data, error } = await api
        .from('profiles')
        .select('id, full_name')
        .eq('is_active', true)
        .order('full_name');
      if (error) throw error;
      return data as { id: string; full_name: string }[];
    },
  });

  const form = useForm<EPIAssignmentFormData>({
    resolver: zodResolver(epiAssignmentFormSchema),
    defaultValues: {
      epi_inventory_id: preselectedEpiId || '',
      employee_id: preselectedEmployeeId || '',
      expected_return_date: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        epi_inventory_id: preselectedEpiId || '',
        employee_id: preselectedEmployeeId || '',
        expected_return_date: '',
        notes: '',
      });
    }
  }, [open, preselectedEpiId, preselectedEmployeeId, form]);

  const onSubmit = async (data: EPIAssignmentFormData) => {
    setLoading(true);
    try {
      await assignMutation.mutateAsync({
        epi_inventory_id: data.epi_inventory_id,
        employee_id: data.employee_id,
        expected_return_date: data.expected_return_date || undefined,
        notes: data.notes || undefined,
      });

      onOpenChange(false);
      form.reset();
    } catch (error) {
      // Error handled by mutation
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Asignar EPI</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="epi_inventory_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>EPI a asignar *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={!!preselectedEpiId}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar EPI..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableEpis?.map((epi) => (
                        <SelectItem key={epi.id} value={epi.id}>
                          {epi.epi_type?.name} {epi.serial_number && `- ${epi.serial_number}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="employee_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Empleado *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={!!preselectedEmployeeId}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar empleado..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employees?.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="expected_return_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha devolucion esperada</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Asignar
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
